import { displayMarginPercent, getMarginBucket, hasIncompleteCost, MARGIN_BUCKET_THRESHOLDS } from '../profitabilityAnalysis/analysis';
import { forecastMonthsLabel, monthShortLabel } from '../incomeStatement/incomeStatement';
import type { IncomeStatementResponse } from '../incomeStatement/incomeStatementApi';

// Order portfolio of the "Pulpit zarządu" from the regular order list
// (GET /orders, forecast basis = the order's current fields, same as the
// "Analiza" tab). Statuses are informational (WP3); the groups below only
// arrange them for the chart.

export interface DashboardOrder {
  id: string;
  orderNumber: string;
  manualOrderNumber?: string;
  useManualNumbering?: boolean;
  status: string;
  customer?: { id?: string; name: string };
  revenue: number;
  totalCost: number | null;
  margin: number | null;
  marginPercent: number | null;
  warehouseCostPending?: boolean;
  orderDate?: string;
  effectiveForecastMonth?: string;
  expectedDelivery?: string;
}

export const PORTFOLIO_GROUPS = [
  { key: 'draft', statuses: ['NEW_INQUIRY', 'QUOTE_SENT'] },
  { key: 'confirmed', statuses: ['CONFIRMED'] },
  { key: 'fulfillment', statuses: ['IN_FULFILLMENT'] },
  { key: 'ready', statuses: ['READY_FOR_DISPATCH'] },
  { key: 'delivery', statuses: ['IN_DELIVERY', 'PARTIALLY_DELIVERED'] },
  { key: 'delivered', statuses: ['DELIVERED'] },
  { key: 'onHold', statuses: ['ON_HOLD'] },
  { key: 'invoiced', statuses: ['INVOICED'] },
] as const;

export type PortfolioGroupKey = typeof PORTFOLIO_GROUPS[number]['key'];

/** Confirmed and not yet closed by an invoice — the order portfolio in progress. */
export const OPEN_ORDER_STATUSES = [
  'CONFIRMED', 'IN_FULFILLMENT', 'READY_FOR_DISPATCH', 'IN_DELIVERY', 'PARTIALLY_DELIVERED', 'DELIVERED', 'ON_HOLD',
];

/**
 * „Portfel” of „Sprzedaż i zamówienia” (owner 2026-10-30): the open orders and
 * those closed by an invoice, added together.
 */
export const PORTFOLIO_ORDER_STATUSES = [...OPEN_ORDER_STATUSES, 'INVOICED'];

export function displayOrderNumber(order: Pick<DashboardOrder, 'orderNumber' | 'manualOrderNumber' | 'useManualNumbering'>): string {
  return order.useManualNumbering && order.manualOrderNumber ? order.manualOrderNumber : order.orderNumber;
}

export interface PortfolioGroup {
  key: PortfolioGroupKey;
  count: number;
  revenue: number;
}

export function portfolioByGroup(orders: DashboardOrder[]): PortfolioGroup[] {
  return PORTFOLIO_GROUPS.map((group) => {
    const members = orders.filter((order) => (group.statuses as readonly string[]).includes(order.status));
    return { key: group.key, count: members.length, revenue: members.reduce((sum, order) => sum + order.revenue, 0) };
  });
}

export interface OpenPortfolio {
  orders: DashboardOrder[];
  count: number;
  revenue: number;
  /**
   * Margin % = margin / revenue of the orders (decision 2026-10-29 pt 8:
   * the percent of the amounts shown); an incomplete cost counts its known
   * part and `pendingCostCount` marks the percent as overstated.
   */
  marginPercent: number | null;
  pendingCostCount: number;
}

function portfolioOf(orders: DashboardOrder[], statuses: readonly string[]): OpenPortfolio {
  const members = orders.filter((order) => statuses.includes(order.status));
  const revenue = members.reduce((sum, order) => sum + order.revenue, 0);
  // An unknown cost counts its known part 0 (margin = revenue), like the pivot.
  const margin = members.reduce((sum, order) => sum + (order.margin ?? order.revenue), 0);
  return {
    orders: members,
    count: members.length,
    revenue,
    marginPercent: revenue > 0 ? (margin / revenue) * 100 : null,
    pendingCostCount: members.filter((order) => order.margin == null || hasIncompleteCost(order)).length,
  };
}

export function openPortfolio(orders: DashboardOrder[]): OpenPortfolio {
  return portfolioOf(orders, OPEN_ORDER_STATUSES);
}

export interface OrderPortfolio extends OpenPortfolio {
  open: OpenPortfolio;
  invoiced: OpenPortfolio;
}

/** Open and invoice-closed orders together, with both parts. */
export function orderPortfolio(orders: DashboardOrder[]): OrderPortfolio {
  return {
    ...portfolioOf(orders, PORTFOLIO_ORDER_STATUSES),
    open: openPortfolio(orders),
    invoiced: portfolioOf(orders, ['INVOICED']),
  };
}

export interface ForecastRisks {
  incompleteCost: number;
  noCost: number;
  lowMargin: number;
  openQuotes: number;
}

export function forecastRisks(orders: DashboardOrder[], quotes: DashboardOrder[]): ForecastRisks {
  const open = orders.filter((order) => OPEN_ORDER_STATUSES.includes(order.status));
  return {
    incompleteCost: open.filter((order) => getMarginBucket(order) === 'incomplete').length,
    noCost: open.filter((order) => getMarginBucket(order) === 'none').length,
    lowMargin: open.filter((order) => {
      const bucket = getMarginBucket(order);
      return bucket === 'negative' || bucket === 'low';
    }).length,
    openQuotes: quotes.filter((quote) => quote.status !== 'CANCELLED').length,
  };
}

export function quotesValue(quotes: DashboardOrder[]): number {
  return quotes.filter((quote) => quote.status !== 'CANCELLED').reduce((sum, quote) => sum + quote.revenue, 0);
}

/** Open orders ranked by value for the profitability table. */
export function topOpenOrders(orders: DashboardOrder[], limit: number): DashboardOrder[] {
  return orders
    .filter((order) => OPEN_ORDER_STATUSES.includes(order.status))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit);
}

export function orderMarginPercent(order: DashboardOrder): number | null {
  if ((order.totalCost ?? 0) <= 0) return null;
  return displayMarginPercent(order);
}

export const LOW_MARGIN_THRESHOLD = MARGIN_BUCKET_THRESHOLDS.low;

/**
 * Delivery months of each ZK (forecast by delivery, 2026-10-16) from the
 * income statement of the dashboard year: the list of orders carries no
 * tranches, and the rule lives in one place (backend orderForecastTranches).
 * Months outside the year are not in that report.
 */
export function forecastMonthsByOrder(statement: Pick<IncomeStatementResponse, 'customers'>): Map<string, string[]> {
  const byOrder = new Map<string, string[]>();
  for (const customer of statement.customers) {
    for (const order of customer.orders) {
      if (order.forecastMonths.length > 0) byOrder.set(order.id, order.forecastMonths);
    }
  }
  return byOrder;
}

/** "Miesiąc prognozy" cell: delivery months, else the ZK forecast month. */
export function orderForecastMonthsText(
  order: Pick<DashboardOrder, 'id' | 'effectiveForecastMonth'>,
  monthsByOrder: Map<string, string[]>,
  language: string,
): string {
  const delivery = forecastMonthsLabel(monthsByOrder.get(order.id) ?? [], language);
  if (delivery) return delivery;
  return order.effectiveForecastMonth ? monthShortLabel(order.effectiveForecastMonth, language) : '—';
}
